/**
 * Jobs Resource for managing asynchronous operations
 */

import type { ZapSEA } from '../client';
import { JobStatusResponse, JobListResponse, JobListRequest } from '../types';
import { JobNotFoundError, TimeoutError } from '../errors';
import { validateJobId, delay } from '../utils';

export class JobsResource {
  constructor(private client: ZapSEA) {}

  /**
   * Get job status and results
   * 
   * @example
   * ```typescript
   * const status = await client.jobs.get('job_abc123');
   * 
   * if (status.status === 'completed') {
   *   console.log('Results:', status.resultData);
   * }
   * ```
   */
  async get(jobId: string): Promise<JobStatusResponse> {
    if (!validateJobId(jobId)) {
      throw new Error('Invalid job ID format');
    }

    try {
      return await this.client.request<JobStatusResponse>('GET', `/v2/jobs/${jobId}`);
    } catch (error: any) {
      if (error.status === 404) {
        throw new JobNotFoundError(`Job ${jobId} not found`);
      }
      throw error;
    }
  }

  /**
   * Wait for job completion with polling
   * 
   * @example
   * ```typescript
   * const result = await client.jobs.waitForCompletion('job_abc123', {
   *   timeout: 300000,  // 5 minutes
   *   pollInterval: 5000  // 5 seconds
   * });
   * 
   * console.log('Final result:', result.resultData);
   * ```
   */
  async waitForCompletion(
    jobId: string,
    options: {
      timeout?: number;
      pollInterval?: number;
      onProgress?: (status: JobStatusResponse) => void;
    } = {}
  ): Promise<JobStatusResponse> {
    const { timeout = 300000, pollInterval = 5000, onProgress } = options;
    
    if (!validateJobId(jobId)) {
      throw new Error('Invalid job ID format');
    }

    if (timeout < 1000) {
      throw new Error('Timeout must be at least 1000ms');
    }

    if (pollInterval < 1000) {
      throw new Error('Poll interval must be at least 1000ms');
    }

    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      const status = await this.get(jobId);
      
      if (onProgress) {
        onProgress(status);
      }

      if (status.status === 'completed') {
        return status;
      } else if (status.status === 'failed') {
        throw new Error(`Job ${jobId} failed: ${status.errorMessage || 'Unknown error'}`);
      } else if (status.status === 'cancelled') {
        throw new Error(`Job ${jobId} was cancelled`);
      }

      await delay(pollInterval);
    }

    throw new TimeoutError(`Job ${jobId} did not complete within ${timeout}ms`);
  }

  /**
   * List user's jobs
   * 
   * @example
   * ```typescript
   * const jobs = await client.jobs.list({
   *   statusFilter: 'completed',
   *   limit: 10
   * });
   * 
   * jobs.forEach(job => {
   *   console.log(`${job.jobId}: ${job.status}`);
   * });
   * ```
   */
  async list(options: JobListRequest = {}): Promise<JobStatusResponse[]> {
    this._validateListRequest(options);

    const params = new URLSearchParams();
    
    if (options.statusFilter) {
      params.append('status', options.statusFilter);
    }
    
    if (options.limit) {
      params.append('limit', options.limit.toString());
    }

    if (options.offset) {
      params.append('offset', options.offset.toString());
    }

    const queryString = params.toString();
    const endpoint = `/v2/jobs${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.client.request<JobListResponse>('GET', endpoint);
    return response.jobs;
  }

  /**
   * Cancel a running job
   * 
   * @example
   * ```typescript
   * await client.jobs.cancel('job_abc123');
   * console.log('Job cancelled successfully');
   * ```
   */
  async cancel(jobId: string): Promise<void> {
    if (!validateJobId(jobId)) {
      throw new Error('Invalid job ID format');
    }

    await this.client.request('POST', `/v2/jobs/${jobId}/cancel`);
  }

  /**
   * Get job logs for debugging
   * 
   * @example
   * ```typescript
   * const logs = await client.jobs.getLogs('job_abc123');
   * console.log('Job logs:', logs);
   * ```
   */
  async getLogs(jobId: string): Promise<any> {
    if (!validateJobId(jobId)) {
      throw new Error('Invalid job ID format');
    }

    return this.client.request('GET', `/v2/jobs/${jobId}/logs`);
  }

  /**
   * Stream job progress updates
   * 
   * @example
   * ```typescript
   * for await (const update of client.jobs.streamProgress('job_abc123')) {
   *   console.log(`Progress: ${update.progressPercentage}%`);
   *   if (update.status === 'completed') break;
   * }
   * ```
   */
  async* streamProgress(jobId: string, pollInterval: number = 3000): AsyncGenerator<JobStatusResponse> {
    if (!validateJobId(jobId)) {
      throw new Error('Invalid job ID format');
    }

    if (pollInterval < 1000) {
      throw new Error('Poll interval must be at least 1000ms');
    }

    while (true) {
      const status = await this.get(jobId);
      yield status;

      if (['completed', 'failed', 'cancelled'].includes(status.status)) {
        break;
      }

      await delay(pollInterval);
    }
  }

  /**
   * Validate list request parameters
   */
  private _validateListRequest(options: JobListRequest): void {
    if (options.statusFilter) {
      const validStatuses = ['processing', 'completed', 'failed', 'cancelled'];
      if (!validStatuses.includes(options.statusFilter)) {
        throw new Error(`Status filter must be one of: ${validStatuses.join(', ')}`);
      }
    }

    if (options.limit !== undefined) {
      if (typeof options.limit !== 'number' || options.limit < 1 || options.limit > 100) {
        throw new Error('Limit must be a number between 1 and 100');
      }
    }

    if (options.offset !== undefined) {
      if (typeof options.offset !== 'number' || options.offset < 0) {
        throw new Error('Offset must be a non-negative number');
      }
    }
  }
}
